import { View } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import { colors, crewLabels, formatLongDate, formatMinutes, formatTime, parseDate, pick, reasonLabels, statusLabels, type ReportSummary } from '@hogar/shared';
import { Loading, Txt, useT } from '../../components/ui';
import { Logo } from '../../components/Logo';
import { Box, Paper } from '../../components/Paper';
import { statusTextColor } from '../../components/StatusSheet';
import { api } from '../../lib/api';
import { useHouse } from '../../lib/house';

/** A saved daily report from the history, ready to print or save as PDF. */
export default function PastReport() {
  const { date, crew: crewParam } = useLocalSearchParams<{ date: string; crew?: string }>();
  const crew = crewParam === 'garden' ? 'garden' : 'house';
  const { t, lang } = useT();
  const { household } = useHouse();
  const { data: r } = useQuery({ queryKey: ['report', date, crew], queryFn: () => api<ReportSummary>('GET', `/reports/${date}?crew=${crew}`) });
  if (!r) return <Loading />;

  return (
    <Paper>
      <Logo size={0.7} tagline={false} />
      <Txt weight="semibold" size={16} style={{ textAlign: 'center', marginTop: 10 }}>
        {t('reportOf')} · {household.name}{crew === 'garden' ? ` · ${crewLabels.garden[lang]}` : ''}
      </Txt>
      <Txt size={13} color={colors.textMuted} style={{ textAlign: 'center', marginBottom: 14 }}>
        {formatLongDate(parseDate(r.date), lang)}
      </Txt>
      <View style={{ flexDirection: 'row', justifyContent: 'space-around', marginBottom: 14 }}>
        <Stat big={`${r.done}/${r.total}`} small={t('tasksCompleted')} />
        <Stat big={formatMinutes(r.minutes)} small={t('estimatedTime')} />
        <Stat big={String(r.couldNot + r.notReached)} small={t('pending')} />
      </View>
      <View nativeID="print-columns-wide">
      {r.lines.map((l) => (
        <View key={l.taskId} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 5, borderBottomWidth: 1, borderBottomColor: colors.divider }}>
          <Box checked={l.status === 'done'} />
          <View style={{ flex: 1 }}>
            <Txt size={12}>{pick(l.name, lang)}</Txt>
            {l.status !== 'done' && (
              <Txt size={11} color={colors.warningText}>
                {l.status === 'could_not' ? `${t('couldNotDo')}: ${l.reason ? reasonLabels[l.reason][lang] : ''}` : t('notReached')}
                {l.note ? ` · “${pick(l.note, lang)}”` : ''}
              </Txt>
            )}
          </View>
          <Txt size={11} color={colors.textMuted}>
            {l.at ? `${formatTime(l.at)} · ${l.byName || t('formerStaff')}` : '—'}
          </Txt>
        </View>
      ))}
      </View>
      {r.restock.length > 0 && (
        <>
          <Txt weight="semibold" size={13} style={{ marginTop: 14, marginBottom: 4 }}>
            {t('productsToRestock')}
          </Txt>
          {r.restock.map((p, i) => (
            <Txt key={i} size={12} color={statusTextColor[p.status]}>
              • {pick(p.name, lang)}: {statusLabels[p.status][lang]}
            </Txt>
          ))}
        </>
      )}
    </Paper>
  );
}

function Stat({ big, small }: { big: string; small: string }) {
  return (
    <View style={{ alignItems: 'center' }}>
      <Txt weight="semibold" size={17}>
        {big}
      </Txt>
      <Txt size={11} color={colors.textMuted}>
        {small}
      </Txt>
    </View>
  );
}
